import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export default function LCABeregning() {
  const [openFaq, setOpenFaq] = useState(null);

  const lcaFaqs = [
    {
      q: "Skal mit nye enfamiliehus have en LCA-beregning?",
      a: "Ja. Har du søgt byggetilladelse den 1. juli 2025 eller senere, skal et nyt enfamiliehus både have en klimaberegning og overholde grænseværdien på 6,7 kg CO2e pr. m² pr. år samt 1,5 kg for byggeprocessen.",
    },
    {
      q: "Hvornår skal LCA'en afleveres til kommunen?",
      a: "Klimaberegningen indsendes sammen med færdigmeldingen. Den er en forudsætning for ibrugtagningstilladelsen. Vi anbefaler at lave en foreløbig beregning allerede i projekteringen.",
    },
    {
      q: "Hvad koster en LCA-beregning?",
      a: "For et enfamiliehus eller typehus starter prisen fra 7.500 kr. inkl. moms. Erhverv og større byggerier prissættes efter tilbud. Bestiller du LCA sammen med energiramme og energimærke (BR18-pakken), får du 10 % rabat på den samlede pris – også hvis trykprøve lægges med i samme forløb.",
    },
    {
      q: "Hvad skal jeg sende for at få lavet en LCA?",
      a: "Tegninger (plan, snit og facader), en materiale- eller byggebeskrivelse, oplysninger om varme, ventilation og evt. solceller samt gerne en mængdeliste eller en BIM-model. Vi fortæller dig præcis, hvad der mangler.",
    },
    {
      q: "Hvad sker der, hvis mit byggeri ligger over grænseværdien?",
      a: "Så kan byggeriet ikke færdigmeldes, som det er projekteret. Derfor regner vi tidligt og peger på de bygningsdele, der vejer mest, så materialevalget kan justeres i tide.",
    },
    {
      q: "Hvad betyder det, at byggepladsen tæller med?",
      a: "Fra 1. juli 2025 skal transport af materialer og materiel, energi- og brændstofforbrug på pladsen og byggeaffald opgøres og holdes under 1,5 kg CO2e pr. m² pr. år. Entreprenøren skal derfor registrere forbrug og gemme fx affaldsfakturaer. Vi giver en tjekliste fra start.",
    },
    {
      q: "Skal min tilbygning eller min carport have en LCA?",
      a: "Tilbygninger under 250 m² opvarmet etageareal til enfamiliehuse, række-, kæde- og dobbelthuse, stuehuse og sommerhuse er undtaget. Det samme gælder uopvarmede bygninger under 50 m², fx en fritstående carport eller et udhus. Større tilbygninger og tilbygninger til fx erhverv er omfattet.",
    },
    {
      q: "Hvem har ansvaret for, at kravet overholdes?",
      a: "Det har bygningsejeren. I praksis aftales det ofte i kontrakten, at entreprenøren eller typehusfirmaet sørger for dokumentationen. Vi kan levere LCA'en til både bygherre og entreprenør.",
    },
    {
      q: "Hvor lang tid tager en LCA-beregning?",
      a: "Vi leverer LCA-beregningen inden for 5 arbejdsdage efter modtagelse af komplet materiale (tegninger, mængder eller BIM-model). Den endelige LCA laves, når byggeriet er færdigt, og byggepladsdata foreligger.",
    },
    {
      q: "Kan I også lave energirammen, trykprøven og energimærket?",
      a: "Ja. Det er netop fordelen hos os: Én konsulent laver al BR18-dokumentationen, og energiberegningen genbruges direkte i LCA'en.",
    },
  ];

  const lcaServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.etdanmark.com/lca-beregning#service",
    name: "LCA-beregning efter BR18",
    alternateName: "Klimaberegning / livscyklusvurdering af nybyggeri",
    serviceType: "LCA-beregning (klimaberegning) efter bygningsreglementet BR18",
    description:
      "Lovpligtig LCA-beregning (klimaberegning) af nybyggeri efter BR18 §§ 297-298, inkl. byggeprocessen. Leveres alene eller samlet med energiramme, trykprøvning og energimærkning.",
    url: "https://www.etdanmark.com/lca-beregning",
    areaServed: [
      { "@type": "AdministrativeArea", name: "Jylland" },
      { "@type": "AdministrativeArea", name: "Fyn" },
    ],
    provider: {
      "@type": "LocalBusiness",
      "@id": "https://www.etdanmark.com/#organization",
      name: "ET Danmark ApS",
      url: "https://www.etdanmark.com/",
      telephone: "+4542499595",
      email: "info@etdanmark.com",
      vatID: "DK41026138",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Bjerrevej 62 A",
        postalCode: "8700",
        addressLocality: "Horsens",
        addressCountry: "DK",
      },
    },
    offers: {
      "@type": "Offer",
      url: "https://www.etdanmark.com/lca-beregning",
      priceCurrency: "DKK",
      priceSpecification: {
        "@type": "PriceSpecification",
        minPrice: 7500,
        priceCurrency: "DKK",
        valueAddedTaxIncluded: true,
      },
      description:
        "LCA-beregning for enfamiliehus/typehus fra 7.500 kr. inkl. moms (6.000 kr. ekskl. moms). Erhverv og større byggerier efter tilbud. 10 % rabat på den samlede pris ved bestilling sammen med energiramme og energimærke – også hvis trykprøve lægges med i samme forløb. Levering inden for 5 arbejdsdage efter modtagelse af komplet materiale.",
    },
  };

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="LCA-beregning efter BR18 i Jylland & Fyn | ET Danmark"
        description="Lovpligtig LCA-klimaberegning til nybyggeri efter BR18. Samlet med energiramme, trykprøve og energimærke. Jylland & Fyn. Få et tilbud i dag."
        canonical="https://www.etdanmark.com/lca-beregning"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg"
        faqs={lcaFaqs}
        schemas={[lcaServiceSchema]}
      />

      {/* 1. TOP HERO */}
      <PageTopSlider
        title="LCA-beregning efter BR18"
        subtitle="Lovpligtig klimaberegning til dit nybyggeri – samlet med energiramme, trykprøve og energimærke. Få én rådgiver til hele din BR18-dokumentation i Jylland og på Fyn."
        badge="Lovpligtig efter BR18 §§ 297–298"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Ydelser", to: "/#ydelser" },
          { label: "LCA-beregning" },
        ]}
        actions={
          <>
            <a
              href="#kontakt"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-leaf text-xs" aria-hidden="true"></i>
              <span>Få et uforpligtende tilbud på LCA</span>
            </a>
            <a
              href="tel:+4542499595"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Ring til Lars: 42 49 95 95</span>
            </a>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg",
          alt: "LCA beregning og klimaberegning efter BR18",
        }}
      />

      {/* 1.2 INTRO SEKTION */}
      <section className="py-14 sm:py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-scale-balanced" aria-hidden="true"></i>
                <span>Skærpede klimakrav siden 1. juli 2025</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight leading-tight">
                LCA-beregning efter BR18 – klimaberegning til dit nybyggeri
              </h1>
              <div className="space-y-4 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Skal du bygge nyt, kræver bygningsreglementet en LCA-beregning – også kaldet en klimaberegning – før kommunen kan give ibrugtagningstilladelse. Siden 1. juli 2025 skal de fleste nye bygninger desuden holde sig under en fast CO2-grænseværdi, og selve byggepladsen tæller med.
                </p>
                <p>
                  Hos ET Danmark får du LCA-beregningen samme sted som{" "}
                  <Link to="/energiramme" className="text-emerald-700 font-semibold hover:underline">
                    energirammen
                  </Link>
                  ,{" "}
                  <Link to="/trykproevning" className="text-emerald-700 font-semibold hover:underline">
                    trykprøven
                  </Link>{" "}
                  og{" "}
                  <Link to="/energimaerkning" className="text-emerald-700 font-semibold hover:underline">
                    energimærket
                  </Link>
                  . Én energikonsulent, ét sæt tegninger og ét samlet overblik over den BR18-dokumentation, dit byggeri skal bruge. Vi dækker hele Jylland og Fyn.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 pt-3">
                <a
                  href="#kontakt"
                  className="px-6 py-3 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
                  <span>Få et uforpligtende tilbud på LCA</span>
                </a>
                <a
                  href="tel:+4542499595"
                  className="px-6 py-3 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-sm transition-all flex items-center gap-2 border border-stone-300"
                >
                  <i className="fa-solid fa-phone text-emerald-700 text-xs" aria-hidden="true"></i>
                  <span>Ring til Lars: 42 49 95 95</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="p-8 rounded-3xl bg-gradient-to-br from-emerald-950 via-[#11463A] to-[#165B4C] text-white shadow-xl space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-xs flex items-center justify-center text-emerald-300 text-2xl">
                    <i className="fa-solid fa-sparkles" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-emerald-300 uppercase tracking-wider block">
                      Spar 10 % på samlet løsning
                    </span>
                    <h3 className="text-xl font-bold text-white">BR18-pakken til nybyggeri</h3>
                  </div>
                </div>

                <p className="text-stone-200 text-xs sm:text-sm leading-relaxed">
                  Bestil LCA-beregningen sammen med energiramme og energimærke, og få 10 % rabat på den samlede pris – også hvis trykprøve lægges med i samme forløb.
                </p>

                <ul className="space-y-2.5 text-xs text-stone-100 pt-2 border-t border-white/15">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-emerald-400" aria-hidden="true"></i>
                    <span>Energiramme til byggeansøgningen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-emerald-400" aria-hidden="true"></i>
                    <span>LCA-klimaberegning (bygning + byggeproces)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-emerald-400" aria-hidden="true"></i>
                    <span>Trykprøvning (BlowerDoor) & tæthedsrapport</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-emerald-400" aria-hidden="true"></i>
                    <span>Lovpligtigt energimærke til færdigmelding</span>
                  </li>
                </ul>

                <div className="pt-2">
                  <a
                    href="#kontakt"
                    className="w-full py-2.5 px-4 rounded-xl bg-white text-emerald-950 hover:bg-emerald-50 text-xs font-extrabold text-center block transition-colors shadow-xs"
                  >
                    Indhent tilbud på BR18-pakken
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 1.3 HVAD ER EN LCA-BEREGNING? */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Hvad er en LCA-beregning?
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-3 leading-relaxed">
              LCA står for livscyklusvurdering (Life Cycle Assessment). En LCA-beregning opgør, hvor meget CO2 en bygning udleder i hele sin levetid – fra materialerne produceres, over transport og opførelse, udskiftninger og energiforbrug i driften, til bortskaffelse.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg">
                <i className="fa-solid fa-gauge-high" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">kg CO2e / m² / år</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Klimapåvirkningen opgøres i kg CO2-ækvivalenter pr. m² pr. år efter bygningsreglementet (BR18 § 297).
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg">
                <i className="fa-solid fa-hourglass-half" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">50 års betragtning</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Beregningen dækker en betragtningsperiode på 50 år fra bygningens officielle færdigmelding.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg">
                <i className="fa-solid fa-book-bookmark" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">DS/EN 15978</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Udføres efter den anerkendte europæiske standard DS/EN 15978 med standardiserede moduler.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg">
                <i className="fa-solid fa-file-invoice" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Data & EPD'er</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Beregnes med generiske materialedata fra BR18 eller med produktspecifikke miljøvaredeklarationer (EPD'er).
              </p>
            </div>
          </div>

          <div className="mt-8 p-5 rounded-2xl bg-emerald-50/80 border border-emerald-200 text-emerald-950 text-sm flex items-start gap-3">
            <i className="fa-solid fa-circle-info text-emerald-700 mt-0.5 shrink-0" aria-hidden="true"></i>
            <div>
              <strong>Kort sagt:</strong> LCA'en viser, om byggeriet er under grænseværdien – og hvor du kan spare CO2, hvis du ikke er.
            </div>
          </div>
        </div>
      </section>

      {/* 1.4 HVEM SKAL HAVE EN LCA – OG HVORNÅR? */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">
              <i className="fa-solid fa-calendar-check text-emerald-600" aria-hidden="true"></i>
              Gældende regler
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Hvem skal have en LCA – og hvornår?
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-3 leading-relaxed">
              Kravet gælder nybyggeri. Har du søgt byggetilladelse den 1. juli 2025 eller senere, skal næsten alt nybyggeri både have en klimaberegning og overholde en grænseværdi. Det gælder også enfamiliehuse, typehuse, rækkehuse, sommerhuse, erhvervsbyggeri og lagerhaller.
            </p>
          </div>

          {/* Grænseværdier tabel */}
          <div className="bg-[#FAF9F6] border border-stone-200 rounded-3xl overflow-hidden shadow-xs mb-8">
            <div className="p-6 bg-stone-100/80 border-b border-stone-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-stone-900 text-lg">
                  Grænseværdier fra 1. juli 2025 (kg CO2e pr. m² pr. år)
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Gælder for ansøgninger om byggetilladelse indsendt fra 1. juli 2025. Kilde: Bygningsreglementet §§ 297–298.
                </p>
              </div>
              <span className="text-xs font-semibold text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full whitespace-nowrap self-start sm:self-center">
                Lovkrav i BR18
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50/50 text-stone-600 uppercase text-[11px] tracking-wider">
                    <th className="py-3.5 px-6 font-semibold">Bygningstype</th>
                    <th className="py-3.5 px-6 font-semibold text-right sm:text-left">
                      Grænseværdi (materialer + drift)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200 text-stone-800">
                  <tr className="hover:bg-white transition-colors">
                    <td className="py-3.5 px-6 font-semibold">
                      Fritliggende enfamiliehuse, stuehuse, række-, kæde- og dobbelthuse
                    </td>
                    <td className="py-3.5 px-6 text-right sm:text-left font-bold text-emerald-800">
                      6,7 kg CO2e/m²/år
                    </td>
                  </tr>
                  <tr className="hover:bg-white transition-colors">
                    <td className="py-3.5 px-6 font-semibold">
                      Sommerhuse og ferieboliger under 150 m²
                    </td>
                    <td className="py-3.5 px-6 text-right sm:text-left font-bold text-emerald-800">
                      4,0 kg CO2e/m²/år
                    </td>
                  </tr>
                  <tr className="hover:bg-white transition-colors">
                    <td className="py-3.5 px-6 font-semibold">
                      Sommerhuse og ferieboliger på mindst 150 m²
                    </td>
                    <td className="py-3.5 px-6 text-right sm:text-left font-bold text-emerald-800">
                      6,7 kg CO2e/m²/år
                    </td>
                  </tr>
                  <tr className="hover:bg-white transition-colors">
                    <td className="py-3.5 px-6 font-semibold">
                      Etageboliger samt kontor, handel, lager o.lign.
                    </td>
                    <td className="py-3.5 px-6 text-right sm:text-left font-bold text-emerald-800">
                      7,5 kg CO2e/m²/år
                    </td>
                  </tr>
                  <tr className="hover:bg-white transition-colors">
                    <td className="py-3.5 px-6 font-semibold">
                      Øvrigt byggeri (fx skoler, institutioner, parkeringshuse)
                    </td>
                    <td className="py-3.5 px-6 text-right sm:text-left font-bold text-emerald-800">
                      8,0 kg CO2e/m²/år
                    </td>
                  </tr>
                  <tr className="bg-emerald-50/50 hover:bg-emerald-50 transition-colors">
                    <td className="py-4 px-6 font-extrabold text-stone-900 flex items-center gap-2">
                      <i className="fa-solid fa-truck-pickup text-emerald-700" aria-hidden="true"></i>
                      <span>Byggeprocessen (transport og byggeplads) – alle typer</span>
                    </td>
                    <td className="py-4 px-6 text-right sm:text-left font-extrabold text-[#165B4C]">
                      1,5 kg CO2e/m²/år (separat grænse)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="p-4 bg-stone-50 border-t border-stone-200 text-xs text-stone-500 italic">
              Grænseværdierne gælder for byggetilladelser søgt fra 1. juli 2025. Der er politisk lagt op til yderligere skærpelse fra 2027 og 2029, men de nye værdier er endnu ikke endeligt fastsat. Kilde: Bygningsreglementet §§ 297–298.
            </div>
          </div>

          {/* Dobbelt grænseværdi forklaring */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 space-y-2.5">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-sm font-bold">
                1
              </div>
              <h4 className="font-bold text-stone-900 text-base">To uafhængige grænseværdier</h4>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Byggeriet skal overholde to grænseværdier: én for materialer, udskiftninger, energiforbrug og affald – og én for selve byggeprocessen, dvs. transport til og fra pladsen, energi- og brændstofforbrug på byggepladsen og byggeaffald.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 space-y-2.5">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-sm font-bold">
                2
              </div>
              <h4 className="font-bold text-stone-900 text-base">Hvornår skal LCA'en afleveres?</h4>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Klimaberegningen indsendes med færdigmeldingen. Uden den kan kommunen ikke udstede ibrugtagningstilladelse. Vi anbefaler dog at regne allerede i projekteringen, så der er tid til at justere materialevalg, hvis resultatet ligger tæt på grænsen.
              </p>
            </div>
          </div>

          {/* Undtagelser */}
          <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200">
            <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation text-stone-500" aria-hidden="true"></i>
              <span>Undtaget fra kravet er bl.a.:</span>
            </h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs sm:text-sm text-stone-700">
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                <span>Uopvarmede bygninger under 50 m² (fx udhuse og carporte)</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                <span>Tilbygninger under 250 m² opvarmet etageareal til enfamiliehuse, stuehuse, række-, kæde- og dobbelthuse samt sommerhuse</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                <span>Landbrugets avls- og driftsbygninger</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                <span>Transportable konstruktioner og midlertidige, flytbare pavilloner</span>
              </li>
            </ul>
            <p className="text-xs text-stone-500 mt-4 pt-3 border-t border-stone-200">
              Renovering og ombygning er ikke omfattet. Er du i tvivl om dit projekt, så ring – vi giver gerne en hurtig vurdering på{" "}
              <a href="tel:+4542499595" className="text-emerald-700 font-semibold hover:underline">
                42 49 95 95
              </a>
              . Læs også vores uddybende artikel:{" "}
              <Link to="/blog/lca-krav-br18-2025" className="text-emerald-700 font-semibold hover:underline">
                LCA-krav i BR18 2025: Hvad betyder det for dit byggeri? →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* 1.5 SÅDAN FOREGÅR DET – TRIN FOR TRIN */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Sådan foregår det – trin for trin
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              En overskuelig og struktureret proces fra dine første tegninger til den færdige rapport klar til kommunen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Trin 1 */}
            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3 relative">
              <span className="w-8 h-8 rounded-full bg-[#165B4C] text-white font-extrabold text-xs flex items-center justify-center">
                1
              </span>
              <h3 className="text-lg font-bold text-stone-900">Uforpligtende henvendelse</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Du sender os tegninger og en kort beskrivelse af projektet. Vi vurderer opgaven og sender en fast pris.
              </p>
            </div>

            {/* Trin 2 */}
            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3 relative">
              <span className="w-8 h-8 rounded-full bg-[#165B4C] text-white font-extrabold text-xs flex items-center justify-center">
                2
              </span>
              <h3 className="text-lg font-bold text-stone-900">Du leverer grundlaget</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Plan-, snit- og facadetegninger (PDF eller BIM/IFC), materiale-/byggebeskrivelse, mængdeliste fra entreprenør, oplysninger om varmekilde, ventilation og eventuelle produktspecifikke EPD'er.
              </p>
            </div>

            {/* Trin 3 */}
            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3 relative">
              <span className="w-8 h-8 rounded-full bg-[#165B4C] text-white font-extrabold text-xs flex items-center justify-center">
                3
              </span>
              <h3 className="text-lg font-bold text-stone-900">Beregning & grænsetjek</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Vi opbygger bygningsmodellen i et moderne beregningsværktøj direkte fra mængdeliste eller BIM. Du får et foreløbigt resultat inden for 5 arbejdsdage efter komplet materiale, så du tidligt ved, om byggeriet holder.
              </p>
            </div>

            {/* Trin 4 */}
            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3 relative">
              <span className="w-8 h-8 rounded-full bg-[#165B4C] text-white font-extrabold text-xs flex items-center justify-center">
                4
              </span>
              <h3 className="text-lg font-bold text-stone-900">Optimering ved behov</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Ligger resultatet tæt på eller over grænsen, peger vi på de bygningsdele, der vejer mest, og foreslår alternativer – fx et andet fundament, en anden isolering eller produkter med EPD.
              </p>
            </div>

            {/* Trin 5 */}
            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3 relative">
              <span className="w-8 h-8 rounded-full bg-[#165B4C] text-white font-extrabold text-xs flex items-center justify-center">
                5
              </span>
              <h3 className="text-lg font-bold text-stone-900">Byggeprocessen dokumenteres</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Undervejs registreres el-, varme- og brændstofforbrug på pladsen, transporter og byggeaffald. Vi fortæller entreprenøren præcis, hvilke data der skal gemmes, så det ikke bliver en jagt på bilag til sidst.
              </p>
            </div>

            {/* Trin 6 */}
            <div className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs space-y-3 relative">
              <span className="w-8 h-8 rounded-full bg-[#165B4C] text-white font-extrabold text-xs flex items-center justify-center">
                6
              </span>
              <h3 className="text-lg font-bold text-stone-900">Endelig LCA til færdigmelding</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Når byggeriet er færdigt, opdaterer vi beregningen med de faktiske materialer og byggepladsdata og leverer den endelige dokumentation, klar til at blive indsendt med færdigmeldingen til kommunen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 1.6 DET FÅR DU */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Det får du hos ET Danmark
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Professionel rådgivning, hurtig svartid og gennemsigtige vilkår.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
            {[
              "En LCA-beregning efter BR18 §§ 297–298, der dækker begge grænseværdier (bygning og byggeproces)",
              "En rapport, der er klar til indsendelse med færdigmeldingen til kommunen",
              "Et foreløbigt resultat i projekteringen, så der er tid til at rette",
              "En konkret liste over de bygningsdele, der påvirker resultatet mest",
              "En tjekliste til entreprenøren over byggepladsdata (energi, transport og affald)",
              "Levering inden for 5 arbejdsdage efter modtagelse af komplet materiale (tegninger, mængder eller BIM-model)",
              "En fast pris, som du kender på forhånd",
              "Én kontaktperson til både LCA, energiramme, trykprøve og energimærke",
            ].map((text, idx) => (
              <div
                key={idx}
                className="p-4 rounded-xl bg-stone-50 border border-stone-200 flex items-start gap-3"
              >
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                <span className="text-xs sm:text-sm font-semibold text-stone-800 leading-relaxed">
                  {text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 1.7 PRISER & BR18-PAKKE */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70" id="priser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">
              <i className="fa-solid fa-tag text-emerald-600" aria-hidden="true"></i>
              Faste og gennemsigtige priser
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Pris på LCA-beregning
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Prisen er inkl. moms og den endelige rapport. LCA-beregningen leveres inden for 5 arbejdsdage efter modtagelse af komplet materiale (tegninger, mængder eller BIM-model).
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Prisliste boks */}
            <div className="lg:col-span-7 bg-white rounded-3xl border border-stone-200 overflow-hidden shadow-xs">
              <div className="p-6 bg-stone-100/70 border-b border-stone-200 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-stone-900 text-lg">Priser på LCA-beregning</h3>
                  <p className="text-xs text-stone-500">Inkl. moms og endelig rapport til færdigmelding</p>
                </div>
                <span className="text-xs font-semibold text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
                  Levering 5 arbejdsdage
                </span>
              </div>

              <div className="divide-y divide-stone-200">
                <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-stone-50/50 transition-colors">
                  <div>
                    <h4 className="font-bold text-stone-900 text-base">Enfamiliehus / typehus</h4>
                    <p className="text-xs text-stone-500">
                      Standard enfamiliehus, villa eller sommerhus
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <span className="text-2xl font-extrabold text-[#165B4C]">Fra 7.500 kr.</span>
                    <span className="text-xs text-stone-500 block">inkl. moms (fra 6.000 kr. ekskl. moms)</span>
                  </div>
                </div>

                <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-stone-50/50 transition-colors">
                  <div>
                    <h4 className="font-bold text-stone-900 text-base">Række-, kæde- og dobbelthuse</h4>
                    <p className="text-xs text-stone-500">
                      Flere ensartede enheder eller samlede rækkehusbebyggelser
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <span className="text-lg font-bold text-stone-900">Pris efter tilbud</span>
                    <span className="text-xs text-stone-500 block">Storkunderabat ved flere enheder</span>
                  </div>
                </div>

                <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-stone-50/50 transition-colors">
                  <div>
                    <h4 className="font-bold text-stone-900 text-base">Erhverv, haller og større byggerier</h4>
                    <p className="text-xs text-stone-500">
                      Kontor, lager, industri og institutioner efter BR18
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <span className="text-lg font-bold text-stone-900">Pris efter tilbud</span>
                    <span className="text-xs text-stone-500 block">Tilpasset projektets kompleksitet</span>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-stone-50/60 border-t border-stone-200 text-xs text-stone-500">
                Se også vores øvrige priser på{" "}
                <Link to="/priser" className="text-emerald-700 font-semibold hover:underline">
                  energimærkning, trykprøvning og termografi på prissiden →
                </Link>
              </div>
            </div>

            {/* BR18-pakke fremhævning */}
            <div className="lg:col-span-5 bg-gradient-to-br from-[#11463A] to-[#165B4C] text-white p-7 rounded-3xl shadow-xl space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/30 text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-sparkles text-[11px]" aria-hidden="true"></i>
                <span>Populært valg – Spar 10 %</span>
              </div>
              <h3 className="text-2xl font-bold text-white tracking-tight">
                BR18-pakke: LCA + Energiramme + Energimærke
              </h3>
              <p className="text-stone-200 text-xs sm:text-sm leading-relaxed">
                Bestil LCA-beregningen sammen med energiramme og energimærke, og få 10 % rabat på den samlede pris – også hvis trykprøve lægges med i samme forløb.
              </p>
              <ul className="space-y-2 text-xs text-stone-100 pt-2 border-t border-white/20">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-300" aria-hidden="true"></i>
                  <span>Al dokumentation samlet hos én energikonsulent</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-300" aria-hidden="true"></i>
                  <span>Samme tegninger genbruges direkte i LCA'en</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-300" aria-hidden="true"></i>
                  <span>Trykprøvning (BlowerDoor) kan tilføjes med samme 10 % rabat</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-300" aria-hidden="true"></i>
                  <span>Ingen risiko for modstridende data eller forsinkelser</span>
                </li>
              </ul>
              <div className="pt-3">
                <a
                  href="#kontakt"
                  className="w-full py-3 px-4 rounded-xl bg-white hover:bg-emerald-50 text-emerald-950 font-extrabold text-xs text-center block transition-colors shadow-sm"
                >
                  Få et skræddersyet pakketilbud
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 1.8 HVORFOR VÆLGE ET DANMARK? */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Hvorfor vælge ET Danmark til din LCA?
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Vi kombinerer dyb byggeteknisk erfaring med personlig service og fuld gennemskuelighed.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg">
                <i className="fa-solid fa-hard-hat" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">20+ års erfaring i byggeriet</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Vi kender byggeriet indefra – fra mange års selvstændigt virke med totalentrepriser. Derfor forstår vi både tegningerne, materialerne og hverdagen på byggepladsen, og vi kan tale med både bygherre, arkitekt og entreprenør.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg">
                <i className="fa-solid fa-cubes" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Al BR18-dokumentation ét sted</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Energiramme, LCA, trykprøve og energimærke hænger tæt sammen. Energiberegningen indgår direkte i LCA'en, og valget af materialer påvirker både energi og CO2. Når én konsulent laver det hele, slipper du for at sende de samme tegninger til flere rådgivere.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg">
                <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Lokal i Jylland og på Fyn</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Vi har base i Horsens og dækker hele Jylland og Fyn. Skal vi alligevel ud til trykprøven og energimærket, er der ingen ekstra kørsel.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg">
                <i className="fa-solid fa-handshake" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Ærlig, fast pris</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Du får en fast pris, før vi går i gang – og ingen skjulte gebyrer. Vi leverer inden for 5 arbejdsdage efter komplet materiale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 1.9 CTA-BOKSE MIDT PÅ SIDEN */}
      <section className="py-14 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* CTA for private/nybyg */}
            <div className="p-8 rounded-3xl bg-white border border-stone-200 shadow-2xs space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-house" aria-hidden="true"></i>
                <span>Private bygherrer</span>
              </div>
              <h3 className="text-xl font-bold text-stone-900">
                Skal du bygge nyt i 2026?
              </h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Få styr på LCA'en fra start. Send dine tegninger, så vender vi tilbage med en fast pris inden for 24 timer. Vi hjælper dig sikkert i mål med ibrugtagningstilladelsen.
              </p>
              <div className="pt-2">
                <a
                  href="#kontakt"
                  className="px-5 py-2.5 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white text-xs font-bold inline-flex items-center gap-2 transition-colors"
                >
                  <span>Send dine tegninger her</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                </a>
              </div>
            </div>

            {/* CTA for professionelle */}
            <div className="p-8 rounded-3xl bg-white border border-stone-200 shadow-2xs space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-stone-100 text-stone-800 text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-briefcase" aria-hidden="true"></i>
                <span>Professionelle samarbejdspartnere</span>
              </div>
              <h3 className="text-xl font-bold text-stone-900">
                Entreprenør, typehusfirma eller arkitekt?
              </h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Vi laver LCA, energiramme og trykprøve på jeres projekter i Jylland og på Fyn – også løbende på flere sager. Vi kan arbejde direkte på BIM-modeller eller mængdelister.
              </p>
              <div className="pt-2">
                <a
                  href="mailto:info@etdanmark.com"
                  className="px-5 py-2.5 rounded-full bg-stone-800 hover:bg-stone-900 text-white text-xs font-bold inline-flex items-center gap-2 transition-colors"
                >
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
                  <span>Skriv til info@etdanmark.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOKALE LCA-LANDINGSSIDER (SEKTION "LCA I DIT OMRÅDE") */}
      <section className="py-14 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              LCA-beregning i dit område
            </h2>
            <p className="text-stone-600 text-xs sm:text-sm mt-2">
              Vi har base i Horsens og dækker hele Jylland og Fyn. Se vores lokale sider for specifikke krav og lokale forhold:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            <Link
              to="/lca-beregning-i-aarhus"
              className="p-5 rounded-2xl bg-stone-50 border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50/30 transition-all text-center group"
            >
              <div className="w-10 h-10 rounded-xl bg-white border border-stone-200 group-hover:bg-emerald-100 text-emerald-800 flex items-center justify-center mx-auto mb-3 transition-colors">
                <i className="fa-solid fa-building text-base" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-sm group-hover:text-emerald-800">
                LCA-beregning i Aarhus
              </h3>
              <p className="text-xs text-stone-500 mt-1">
                Typehuse, udstykninger og arkitekter
              </p>
            </Link>

            <Link
              to="/lca-beregning-i-horsens"
              className="p-5 rounded-2xl bg-stone-50 border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50/30 transition-all text-center group"
            >
              <div className="w-10 h-10 rounded-xl bg-white border border-stone-200 group-hover:bg-emerald-100 text-emerald-800 flex items-center justify-center mx-auto mb-3 transition-colors">
                <i className="fa-solid fa-house text-base" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-sm group-hover:text-emerald-800">
                LCA-beregning i Horsens
              </h3>
              <p className="text-xs text-stone-500 mt-1">
                Lokal energikonsulent på Bjerrevej
              </p>
            </Link>

            <Link
              to="/lca-beregning-i-vejle"
              className="p-5 rounded-2xl bg-stone-50 border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50/30 transition-all text-center group"
            >
              <div className="w-10 h-10 rounded-xl bg-white border border-stone-200 group-hover:bg-emerald-100 text-emerald-800 flex items-center justify-center mx-auto mb-3 transition-colors">
                <i className="fa-solid fa-warehouse text-base" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-sm group-hover:text-emerald-800">
                LCA-beregning i Vejle
              </h3>
              <p className="text-xs text-stone-500 mt-1">
                Erhverv, haller og Trekantområdet
              </p>
            </Link>

            <Link
              to="/lca-beregning-i-odense"
              className="p-5 rounded-2xl bg-stone-50 border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50/30 transition-all text-center group"
            >
              <div className="w-10 h-10 rounded-xl bg-white border border-stone-200 group-hover:bg-emerald-100 text-emerald-800 flex items-center justify-center mx-auto mb-3 transition-colors">
                <i className="fa-solid fa-map-location-dot text-base" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-sm group-hover:text-emerald-800">
                LCA-beregning i Odense
              </h3>
              <p className="text-xs text-stone-500 mt-1">
                Nybyggeri på hele Fyn & private bygherrer
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* 1.10 FAQ – OFTE STILLEDE SPØRGSMÅL */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70" id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Ofte stillede spørgsmål om LCA
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Få svar på de vigtigste spørgsmål om klimaberegning, grænseværdier og byggeprocessen.
            </p>
          </div>

          <div className="space-y-3">
            {lcaFaqs.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-stone-200/90 overflow-hidden shadow-2xs"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 text-left font-bold text-stone-900 flex items-center justify-between gap-4 hover:bg-stone-50/60 transition-colors"
                >
                  <span className="text-sm sm:text-base">{item.q}</span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs text-stone-400 transition-transform duration-200 shrink-0 ${
                      openFaq === idx ? "rotate-180 text-[#165B4C]" : ""
                    }`}
                    aria-hidden="true"
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 pt-1 text-xs sm:text-sm text-stone-600 leading-relaxed border-t border-stone-100 whitespace-pre-line">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ANMELDELSER */}
      <ReviewsSlider />

      {/* AFSLUTTENDE KONTAKTFORMULAR */}
      <section id="kontakt" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                <span>Kom godt i gang</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Klar til at komme i gang med din LCA?
              </h2>

              <p className="text-stone-600 text-sm leading-relaxed">
                Udfyld formularen og vælg "LCA-beregning" eller "BR18-pakke", eller ring på 42 49 95 95. Vi vender tilbage med en fast pris og hurtig afklaring inden for 24 timer.
              </p>

              <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 space-y-3">
                <p className="text-xs text-stone-500 uppercase tracking-wider font-semibold">
                  Direkte kontakt
                </p>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-[#165B4C]" aria-hidden="true"></i>
                  <a
                    href="tel:+4542499595"
                    className="font-bold text-stone-900 hover:text-emerald-700 whitespace-nowrap"
                  >
                    42 49 95 95
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-[#165B4C]" aria-hidden="true"></i>
                  <a
                    href="mailto:info@etdanmark.com"
                    className="text-stone-700 hover:text-emerald-700 whitespace-nowrap text-sm"
                  >
                    info@etdanmark.com
                  </a>
                </div>
                <div className="flex items-center gap-3 text-xs text-stone-500 pt-1">
                  <i className="fa-solid fa-clock text-emerald-600" aria-hidden="true"></i>
                  <span>Åbent alle dage kl. 07.00 – 17.00</span>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-emerald-50/60 border border-emerald-200/70 text-xs text-emerald-950 space-y-1.5">
                <p className="font-bold">Vedhæftning af tegninger:</p>
                <p className="text-stone-600">
                  Du kan uploade dine PDF-tegninger direkte i formularen. Har du BIM-modeller (IFC, Revit) eller store mængdelister i Excel, kan du også sende dem direkte til{" "}
                  <a href="mailto:info@etdanmark.com" className="font-semibold underline">
                    info@etdanmark.com
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm
                defaultService="LCA-beregning"
                title="Få et uforpligtende tilbud på LCA"
                subtitle="Send dine tegninger eller projektbeskrivelse. Vi svarer inden for 24 timer."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
